import { useEffect, useState } from 'react';
import type { VideoWall, VideoWallOperations, VideoWallPublication } from '@ghostscreen/contracts';
import { useTranslation } from 'react-i18next';
import { formatNumber, formatTime } from '../../i18n';
import { formatAge, manifestLabel, publishStep } from './wallFormat';

type WallPublishCardProps = {
  publication: VideoWallPublication | null;
  /** Server-computed wall state. The card only displays it. */
  operations: VideoWallOperations | null;
  mode: VideoWall['mode'] | null;
  canPublish: boolean;
  busy: boolean;
  onPublish: () => void;
  onStop: () => void;
  onRefresh: () => void;
  /** When the wall state was last fetched (ms epoch), for "Updated X s ago". */
  updatedAt?: number | null;
};

const STEPS = ['prepare', 'ready', 'commit'] as const;

export function WallPublishCard({
  publication,
  operations,
  mode,
  canPublish,
  busy,
  onPublish,
  onStop,
  onRefresh,
  updatedAt = null,
}: WallPublishCardProps) {
  const { t } = useTranslation(['videoWalls', 'common']);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!updatedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [updatedAt]);
  const state = operations?.state ?? 'no_content';
  const step = publishStep(state);
  const failed = step === 'failed';
  // A failed publish stops on step 1 shown as Failed, never a permanent Prepare.
  const activeIndex =
    step === 'prepare' || failed ? 0 : step === 'ready' ? 1 : step === 'commit' ? 2 : -1;
  const readyTiles = operations?.liveReadyTileCount ?? 0;
  // Interrupted copy counts only tiles that are live AND READY, matching the server state.
  const liveReadyTiles = operations?.liveReadyTileCount ?? 0;
  const requiredTiles = operations?.requiredTileCount ?? 0;
  const canStartNew =
    !publication || state === 'no_content' || state === 'stopped' || state === 'failed';
  const isLive = state === 'starting' || state === 'on_air' || state === 'interrupted';

  const phaseHint = (() => {
    switch (state) {
      case 'slicing':
        return mode === 'mirror' ? t('publish.phase.validating') : t('publish.phase.prepare');
      case 'failed':
        return t('status.failed');
      case 'waiting_for_tiles':
        return t('publish.phase.ready');
      case 'starting':
        return t('status.starting');
      case 'on_air':
        return t('status.onAir');
      case 'interrupted':
        return t('status.interrupted');
      case 'no_screens':
        return t('status.noScreens');
      case 'stopped':
        return t('status.stopped');
      default:
        return t('publish.phase.none');
    }
  })();

  const copy = (() => {
    if (!publication || state === 'no_content') {
      return t('publish.copy.none');
    }
    switch (state) {
      case 'slicing':
        return mode === 'mirror' ? t('publish.copy.slicingMirror') : t('publish.copy.slicing');
      case 'failed':
        return publication.errorMessage ?? t('publish.copy.failed');
      case 'no_screens':
        return t('publish.copy.noScreens');
      case 'waiting_for_tiles':
        return t('publish.copy.ready', { ready: readyTiles, count: requiredTiles });
      case 'starting':
        return t('publish.copy.starting');
      case 'on_air':
        return publication.epochStartAt
          ? t('publish.copy.committed', { time: formatTime(publication.epochStartAt) })
          : t('publish.copy.onAir');
      case 'interrupted':
        return operations && !operations.hasLeader
          ? t('publish.copy.interruptedNoLeader', { ready: liveReadyTiles, count: requiredTiles })
          : t('publish.copy.interrupted', { ready: liveReadyTiles, count: requiredTiles });
      default:
        return t('publish.copy.stopped');
    }
  })();

  const primaryLabel = (() => {
    if (canStartNew) {
      if (busy) return t('publish.actions.publishing');
      return failed ? t('publish.actions.publishAgain') : t('publish.actions.collectReady');
    }
    if (state === 'waiting_for_tiles') {
      return busy
        ? t('publish.actions.refreshing')
        : t('publish.actions.commit', { manifest: manifestLabel(publication) });
    }
    if (isLive) return t('publish.actions.republish');
    return busy ? t('common:actions.working') : t('publish.actions.refreshReady');
  })();

  const onPrimary = () => {
    if (canStartNew || isLive) {
      onPublish();
      return;
    }
    onRefresh();
  };

  return (
    <article
      className="overflow-hidden rounded-[14px] border"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <div
        className="flex flex-wrap items-center gap-3 border-b px-[22px] py-[17px]"
        style={{ borderColor: 'var(--gs-line)' }}
      >
        <h3 className="m-0 text-base font-bold">{t('publish.title')}</h3>
        {publication ? (
          <span
            className="rounded-full border px-2.5 py-1 font-mono text-xs font-semibold"
            style={{
              color: 'var(--gs-ink2)',
              background: 'var(--gs-panel2)',
              borderColor: 'var(--gs-line)',
            }}
          >
            {t('publish.manifest', { manifest: manifestLabel(publication) })}
          </span>
        ) : null}
        <span
          className="ms-auto text-[12.5px]"
          style={{ color: failed ? 'var(--gs-bad)' : 'var(--gs-muted)' }}
        >
          {phaseHint}
        </span>
      </div>

      <div className="flex flex-col gap-[18px] px-[22px] py-5">
        <div className="flex items-center">
          {STEPS.map((item, index) => {
            const active = index === activeIndex;
            const done = activeIndex > index;
            const failedHere = failed && index === 0;
            const ring = failedHere
              ? 'var(--gs-bad)'
              : active || done
                ? 'var(--gs-accent)'
                : 'var(--gs-line)';
            const fill = failedHere
              ? 'var(--gs-bad)'
              : active
                ? 'var(--gs-accent-solid)'
                : done
                  ? 'var(--gs-panel2)'
                  : 'transparent';
            const ink = failedHere
              ? 'var(--gs-panel)'
              : active
                ? 'var(--gs-accent-ink)'
                : done
                  ? 'var(--gs-accent)'
                  : 'var(--gs-muted)';
            const labelColor = failedHere
              ? 'var(--gs-bad)'
              : active || done
                ? 'var(--gs-ink)'
                : 'var(--gs-muted)';
            const barColor = done || active ? 'var(--gs-accent)' : 'var(--gs-line)';
            return (
              <span key={item} className="flex min-w-0 flex-1 items-center">
                <span className="flex shrink-0 flex-col items-center gap-1.5">
                  <span
                    className="grid h-[30px] w-[30px] place-items-center rounded-full border-2 text-xs font-extrabold"
                    style={{ borderColor: ring, background: fill, color: ink }}
                  >
                    {failedHere ? '!' : formatNumber(index + 1)}
                  </span>
                  <span
                    className="whitespace-nowrap text-[11.5px] font-bold uppercase tracking-[0.1em]"
                    style={{ color: labelColor }}
                  >
                    {failedHere ? t('status.failed') : t(`publish.steps.${item}`)}
                  </span>
                </span>
                {index < STEPS.length - 1 ? (
                  <span
                    className="mx-2 mb-5 h-0.5 flex-1"
                    style={{ background: failedHere ? 'var(--gs-line)' : barColor }}
                  />
                ) : null}
              </span>
            );
          })}
        </div>

        <p
          className="m-0 text-[13px] leading-[1.5]"
          style={{ color: failed || state === 'interrupted' ? 'var(--gs-bad)' : 'var(--gs-ink2)' }}
        >
          {copy}
        </p>
        {updatedAt ? (
          <p
            className="m-0 text-[12px]"
            style={{ color: 'var(--gs-ink3, var(--gs-ink2))' }}
            data-testid="wall-updated-ago"
          >
            {t('publish.updatedAgo', { age: formatAge(new Date(updatedAt).toISOString(), now) })}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2.5">
          <button
            type="button"
            disabled={busy || (canStartNew && !canPublish)}
            onClick={onPrimary}
            className="rounded-[10px] border-none px-[18px] py-3 text-sm font-bold disabled:opacity-50"
            style={{
              color: 'var(--gs-accent-ink)',
              background: 'var(--gs-accent-solid)',
              fontFamily: 'inherit',
            }}
          >
            {primaryLabel}
          </button>
          {publication && publication.status !== 'stopped' ? (
            <button
              type="button"
              disabled={busy}
              onClick={onStop}
              className="rounded-[10px] border px-4 py-3 text-sm font-semibold disabled:opacity-50"
              style={{
                color: 'var(--gs-ink2)',
                background: 'var(--gs-panel2)',
                borderColor: 'var(--gs-line)',
                fontFamily: 'inherit',
              }}
            >
              {t('publish.actions.reset')}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
